import React from 'react';
import {
  Column,
  Container,
  Flex,
  Heading,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { FormProvider, useForm } from 'react-hook-form/dist/index.ie11';
import { useSearchCorporateAccountsByParentAdmin } from 'src/components/enterprise/Parents/Admin/hooks/useSearchCorporateAccountsByParentAdmin/useSearchCorporateAccountsByParentAdmin';
import { CorporateAccountsRow } from 'src/components/enterprise/Parents/Admin/CorporateAccountsController/CorporateAccountsRow/CorporateAccountsRow';
import { TieredAccountsTable } from 'src/components/enterprise/Parents/Admin/TieredAccountTable/TieredAccountsTable';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { TableSearchForm } from 'src/components/enterprise/Parents/TableSearchForm/TableSearchForm';
import { ParentAdminTableFormProps } from 'src/components/enterprise/Parents/Admin/ParentAdminsController/types';
import { ISearchParams } from 'src/utilities/useSearchHooks';
import { NumberOfCorporateAccounts } from 'src/components/enterprise/Parents/Admin/CorporateAccountsController/NumberOfCorporateAccounts/NumberOfCorporateAccounts';
import { TIERED_ACCOUNTS_PAGE_SIZE } from 'src/components/enterprise/Parents/constants';
import { Pagination } from 'src/components/Pagination/Pagination';

export const CorporateAccountsController: React.FC = () => {
  const { user } = useAuth();

  const dinerId = user.ud_id;

  const {
    searchResults: {
      resultsList,
      firstItemNumberFromPage,
      lastItemNumberFromPage,
      totalResults,
      totalPages,
      currentSearchPage,
      doSearch,
    },
  } = useSearchCorporateAccountsByParentAdmin(
    dinerId,
    TIERED_ACCOUNTS_PAGE_SIZE
  );

  const formProps = useForm<ParentAdminTableFormProps>({
    defaultValues: {
      searchInput: '',
    },
  });

  const handleSearchWithParams = (query: string, page = 1) => {
    const params: ISearchParams = { page };
    if (query) {
      params.query = query;
    }

    doSearch(params);
  };

  const handleSearchClick = () => {
    const query = formProps.getValues('searchInput');
    handleSearchWithParams(query);
  };

  const handlePageChange = (page = 1) => {
    const query = formProps.getValues('searchInput');
    handleSearchWithParams(query, page);
  };

  const noResultsMsg = 'No corporate accounts';
  const headers = ['Name', 'Account type'];

  return (
    <Container variant="default">
      <PageHeader>
        <Stack y="lg">
          <Heading variant="h2">Corporate Accounts</Heading>
        </Stack>
      </PageHeader>

      <Stack y="lg">
        <PageSection>
          <FormProvider {...formProps}>
            <Stack y="md">
              <Row>
                <Column phone={4}>
                  <TableSearchForm
                    onSearchClick={handleSearchClick}
                    placeholder="Search corporate account"
                  />
                </Column>
              </Row>
            </Stack>
            <Stack y="lg">
              <NumberOfCorporateAccounts
                firstItemNumberFromPage={firstItemNumberFromPage}
                lastItemNumberFromPage={lastItemNumberFromPage}
                totalResults={totalResults}
              />
            </Stack>
            <Stack y="md">
              <TieredAccountsTable
                numberOfAccounts={resultsList.length}
                noResultsMsg={noResultsMsg}
                headers={headers}
                scrollable={false}
              >
                {resultsList.map(({ name, id, sign_up_type }) => (
                  <CorporateAccountsRow
                    key={id}
                    name={name}
                    accountType={sign_up_type}
                    corpId={id}
                  />
                ))}
              </TieredAccountsTable>
            </Stack>
            {totalPages > 1 && (
              <Flex justifyContent="center">
                <Pagination
                  page={currentSearchPage}
                  pageSize={TIERED_ACCOUNTS_PAGE_SIZE}
                  ellipses={false}
                  rotate
                  collectionSize={totalResults}
                  pageChange={handlePageChange}
                />
              </Flex>
            )}
          </FormProvider>
        </PageSection>
      </Stack>
    </Container>
  );
};
